import { createSlice } from '@reduxjs/toolkit';
import { stripMongoProperties } from 'client/api-utils';
import type { TGetState } from 'client/store/configureStore';
import { apiCallBegan } from 'client/store/middleware/api/api';
import { Method } from 'common/enums/common';
import { IAnalytic, IDashboard } from 'common/interfaces/analytics';
import { IQuery, IQueryResult } from 'common/interfaces/query';
import { type Dispatch as ReduxDispatch } from 'redux';
import { getDashboardAnalytics } from './selectors';

type TInitialState = {
  selectedDashboardId: string;
  dashboards: IDashboard[],
  isFetching: boolean,
  filters: {
    [key: string]: {
      [key: string]: string
    };
  }
  analyticResults: {
    [k: string]: { result: IQueryResult, query: IQuery, runDate: string }
  },
  analyticRunState: {
    [k: string]: boolean
  },
  isChangingAnalytic: {
    [k: string]: boolean
  },
};
const initialState: TInitialState = {
  selectedDashboardId: '',
  dashboards: [] as IDashboard[],
  isFetching: true,
  isChangingAnalytic: {},
  analyticResults: {},
  analyticRunState: {},
  filters: {},
};

const analytics = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    analyticsReceived: (state, action) => {
      const { dashboards } = state;
      const { dashboardid, analytics } = action.payload;
      const index = state.dashboards.findIndex(dashboard => dashboard.dashboardid === dashboardid);
      if (dashboards?.[index]) {
        dashboards[index].analytics = analytics;
      }

      state.isFetching = false;
    },
    analyticUpdated: (state, action) => {
      const newAnalytic = action.payload as IAnalytic;
      // @ts-ignore
      const index = state.dashboards.findIndex(dashboard => dashboard._id === newAnalytic.dashboard);

      if (index > 0) {
        const analyticsIndex = state.dashboards?.[index]?.analytics?.findIndex(oldAnalytic => oldAnalytic.analyticid === newAnalytic.analyticid);
        if (analyticsIndex && analyticsIndex >= 0 && state.dashboards[index]?.analytics) {
          state.dashboards[index].analytics[analyticsIndex] = newAnalytic;
          delete state.isChangingAnalytic[newAnalytic.analyticid!];
        }
      }
    },
    analyticsRequested: (state) => {
      state.isFetching = true;
    },
    analyticChangedRequested: (state, action) => {
      const { analyticid } = action.payload;
      state.isChangingAnalytic[analyticid] = true;
    },
    analyticDeleted: (state, action) => {
      const { dashboards } = state;
      const { analyticid, dashboardid } = action.payload;
      const index = state.dashboards.findIndex(dashboard => dashboard._id === dashboardid);
      if (index >= 0 && dashboards[index]?.analytics) {
        const analyticsIndex = dashboards[index]?.analytics.findIndex(analytic => analytic.analyticid === analyticid);
        if (index >= 0 && analyticsIndex >= 0) {
          dashboards[index].analytics.splice(analyticsIndex, 1);
          delete state.isChangingAnalytic[analyticid];
        }
      }
    },
    analyticCreated: (state, action) => {
      const { dashboardid } = action.payload;
      const index = state.dashboards.findIndex(dashboard => dashboard.dashboardid === dashboardid);
      if (index >= 0 && state.dashboards[index].analytics) {
        state.dashboards[index].analytics.push(action.payload);
      }
    },
    analyticsRequestFailed: (state) => {
      state.isFetching = false;
    },
    analyticsRan: (state, action) => {
      const {
        analyticid, result, query, runDate, 
      } = action.payload;
      state.analyticResults[analyticid] = { result, query, runDate };
      delete state.analyticRunState[analyticid];
    },
    cachedAnalyticsRan: (state, action) => {
      const {
        analyticid, result, query, runDate, 
      } = action.payload;
      if (result && query) {
        state.analyticResults[analyticid] = { result, query, runDate };
      }
      delete state.analyticRunState[analyticid];
    },
    analyticsRunRequested: (state, action) => {
      const { analyticid } = action.payload;
      state.analyticRunState[analyticid] = true;
    },
    analyticsRunFailed: (state, action) => {
      const { analyticid } = action.payload;
      delete state.analyticRunState[analyticid];
    },
    analyticFiltersUpdated: (state, action) => {
      const { analyticid, filterValue } = action.payload as IAnalytic & {
        filterValue: {
          [key: string]: string
        }
      };
      if (analyticid) {
        state.filters[analyticid] = { ...filterValue };
      }
    },
    dashboardSelected: (state, action) => {
      state.selectedDashboardId = action.payload;
    },
    dashboardsReceived: (state, action) => {
      state.dashboards = action.payload;
      state.isFetching = false;
    },
    dashboardCreated: (state, action) => {
      state.dashboards.push(action.payload);
    },
    dashboardUpdated: (state, action) => {
      const updatedDashboard = action.payload as IDashboard;
      const index = state.dashboards.findIndex(dashboard => dashboard.dashboardid === updatedDashboard.dashboardid);
      if (index >= 0 && state.dashboards[index]) {
        state.dashboards[index] = updatedDashboard;
      }
    },
    dashboardDeleted: (state, action) => {
      const { dashboards } = state;
      const { dashboardid } = action.payload;
      const index = dashboards.findIndex(dashboard => dashboard.dashboardid === dashboardid);
      if (index >= 0) {
        if (state.dashboards[index].dashboardid === state.selectedDashboardId) {
          state.selectedDashboardId = '';
        }
        dashboards.splice(index, 1);
        state.dashboards = dashboards;
      }
    },
  },
});

export const {
  analyticsReceived,
  dashboardsReceived,
  analyticDeleted,
  analyticCreated,
  analyticUpdated,
  analyticChangedRequested,
  dashboardSelected,
  dashboardCreated,
  dashboardUpdated,
  dashboardDeleted,
  analyticsRunRequested,
  analyticsRunFailed,
  analyticsRan,
  cachedAnalyticsRan,
  analyticFiltersUpdated,
} = analytics.actions;

export default analytics.reducer;

export const createAnalytics = (data: IAnalytic) => apiCallBegan({
  url: '/analytic',
  method: Method.Post,
  data,
  onSuccess: (response: IAnalytic) => ({
    type: analyticCreated.type,
    payload: response,
  }),
});

export const updateAnalytic = (analytic: IAnalytic) => apiCallBegan({
  url: '/analytic',
  method: Method.Post,
  data: stripMongoProperties<IAnalytic>(analytic),
  onStart: () => ({
    type: analyticChangedRequested.type,
    payload: {
      analyticid: analytic.analyticid,
    },
  }),
  onSuccess: () => ({
    type: analyticUpdated.type,
    payload: analytic,
  }),
});

export const deleteAnalytics = (analyticid: string, dashboardid: string) => apiCallBegan({
  url: `/analytic/${analyticid}`,
  method: Method.Delete,
  onStart: () => ({
    type: analyticChangedRequested.type,
    payload: { analyticid },
  }),
  onSuccess: () => ({
    type: analyticDeleted.type,
    payload: { analyticid, dashboardid },
  }),
});

export const createDashboard = (data: IDashboard) => apiCallBegan({
  url: '/analytic/dashboards',
  method: Method.Post,
  data,
  onSuccess: (response: IDashboard) => ({
    type: dashboardCreated.type,
    payload: response,
  }),
});

export const getAnalytics = (dashboardid: string) => {
  return (dispatch: ReduxDispatch, getState: TGetState) => {
    const state = getState();
    const savedAnalytics = getDashboardAnalytics(state);
    if (savedAnalytics?.length) {
      // already in redux do not call server
      return;
    }
    return dispatch(apiCallBegan({
      url: `/analytic/${dashboardid}/analytics`,
      method: Method.Get,
      onSuccess: (response: IAnalytic[]) => ({
        type: analyticsReceived.type,
        payload: { analytics: response, dashboardid },
      }),
    }));
  };
};

export const getAnalyticsDashboards = () => apiCallBegan({
  url: '/analytic/dashboards',
  method: Method.Get,
  onSuccess: (response: IDashboard[]) => ({
    type: dashboardsReceived.type,
    payload: response,
  }),
});

export const updateDashboard = (dashboard: IDashboard) => apiCallBegan({
  url: '/analytic/dashboards',
  method: Method.Post,
  data: stripMongoProperties<IDashboard>(dashboard),
  onSuccess: () => ({
    type: dashboardUpdated.type,
    payload: dashboard,
  }),
});

export const deleteDashboard = (dashboardid: string) => apiCallBegan({
  url: `/analytic/dashboards/${dashboardid}`,
  method: Method.Delete,
  onSuccess: () => ({
    type: dashboardDeleted.type,
    payload: { dashboardid },
  }),
});

export const runAnalytic = (analyticid: string) => apiCallBegan({
  url: `/analytic/${analyticid}/run`,
  method: Method.Post,
  onStart: () => ({
    type: analyticsRunRequested.type,
    payload: { analyticid },
  }),
  onSuccess: (response: { query: IQuery, result: IQueryResult }) => ({
    type: analyticsRan.type,
    payload: response,
  }),
  onError: () => ({
    type: analyticsRunFailed.type,
    payload: { analyticid },
  }),
});

export const fetchCachedResult = (analyticid: string) => apiCallBegan({
  url: `/analytic/${analyticid}/run`,
  method: Method.Get,
  onStart: () => ({
    type: analyticsRunRequested.type,
    payload: { analyticid },
  }),
  onSuccess: (response: { query: IQuery, result: IQueryResult }) => ({
    type: cachedAnalyticsRan.type,
    payload: response,
  }),
  onError: () => ({
    type: analyticsRunFailed.type,
    payload: { analyticid },
  }),
});